import { useState, useEffect, useRef, useCallback } from "react"
import { Stack, Box, IconButton, Tooltip } from "@mui/material"
import { IoClose } from "react-icons/io5";
import { LuSkipBack } from "react-icons/lu";
import SplitView from "../../components/Conversation/SplitView"
import PrepaidForm from "../../components/TestDataForm/Prepaid/PrepaidForm"
import PostpaidForm from "../../components/TestDataForm/Postpaid/PostpaidForm"
import Header from "../../components/Conversation/Header"
import Logo from "../../assets/Images/logo.ico"
import { buildWebSocketUrl } from "../../utils/urlBuilder"
import { useCredentials } from "../../contexts/CredentialsContext"

const GeneralApp = () => {
  let outputMessage = ""
  let outputReferences = []
  const { apiKey, email } = useCredentials()
  const [messages, setMessages] = useState([])
  const [sessionId, setSessionId] = useState(null)
  const [ws, setWs] = useState(null)
  const [wsConnectionId, setWsConnectionId] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [wsStatus, setWsStatus] = useState("connecting")
  const [prepaidFormData, setPrepaidFormData] = useState(null)
  const [postpaidFormData, setPostpaidFormData] = useState(null)
  const [requesterInitialData, setRequesterInitialData] = useState(null)
  const [splitPosition, setSplitPosition] = useState(40) // Default to 50% for chat when form is open
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [showChat, setShowChat] = useState(true)
  const [activeForm, setActiveForm] = useState(null)
  const [lastActiveForm, setLastActiveForm] = useState(null)
  const [streamingMessage, setStreamingMessage] = useState({
    type: "answer",
    message: "",
    message_id: "",
    references: [],
  })
  const messagesEndRef = useRef(null)

  const handleFormControl = (message) => {
    if ((message.toLowerCase().includes('auto-fill') && message.toLowerCase().includes('prepaid')) ||
      message.toLowerCase().includes('prepaid form')) {
      setActiveForm('prepaid');
    } else if (message.toLowerCase().includes('postpaid form')) {
      setActiveForm('postpaid');
    }
  };

  const handleShowForm = (formType) => {
    setActiveForm(formType);
  };

  const handleSendMessage = (message) => {

    if (ws && ws.readyState === WebSocket.OPEN && message) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          type: "prompt",
          message: message,
          message_id: `input_${new Date().getTime()}_${Math.random().toString(36).substring(2, 15)}`,
        },
      ])
      const payload = {
        message: message,
        sessionId: sessionId,
      }
      setIsProcessing(true)
      ws.send(JSON.stringify({ action: "sendMessage", inputText: message, sessionId: sessionId, }))
    }
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support desktop notification")
    } else {
      const permission = await Notification.requestPermission()
      console.log("Notification permission:", permission)
    }
  }

  const handleConnectionLostMessage = () => {
    setMessages((prevMessages) => {
      if (prevMessages.length > 0 && prevMessages[prevMessages.length - 1].type === "prompt") {
        return [...prevMessages, {
          type: "answer",
          message: "Connection lost. Your last message wasn't sent. Please try again.",
          message_id: `connection_error_${new Date().getTime()}`,
        }]
      }
      return prevMessages
    })
  }

  const connectWebSocket = useCallback(() => {
    setWsStatus("connecting")

    let socket
    let wsUrl = process.env.REACT_APP_ASK_IDA_API

    // In dev mode, append user credentials as query params
    if (process.env.REACT_APP_MODE === 'dev' && apiKey && email) {
      wsUrl = buildWebSocketUrl(wsUrl, apiKey, email)
      console.log('🔐 WebSocket connecting with credentials:', { apiKey: apiKey?.substring(0, 10) + '...', email })
    } else if (process.env.REACT_APP_MODE === 'dev') {
      console.warn('⚠️ WebSocket connecting WITHOUT credentials - apiKey or email missing', { apiKey: !!apiKey, email: !!email })
    }

    console.log('🔗 WebSocket URL:', wsUrl)
    socket = new WebSocket(wsUrl)

    setWs(socket)

    socket.addEventListener("open", (event) => {
      setIsProcessing(false)

      console.log("WebSocket is open now.")
      setWsStatus("connected")
    })

    socket.addEventListener("error", (event) => {
      console.log("WebSocket connection error", event)
      setWsStatus("error")

      setIsProcessing(false)
    })

    socket.addEventListener("close", (event) => {
      console.log("WebSocket connection closed", event)
      setWsStatus("closed")

      handleConnectionLostMessage()

      setTimeout(() => {
        console.log("Attempting to reconnect...")
        setWsStatus("reconnecting")
        connectWebSocket()
      }, 5000)
    })

    socket.addEventListener("message", (event) => {
      console.log("Message received from server:", event.data);

      const json_data = JSON.parse(event.data);

      if (json_data.connectionID) {
        console.log("Setting connection ID:", json_data.connectionID)
        setWsConnectionId(json_data.connectionID)
      }

      if (json_data.type === "bedrockSRTicket" && json_data.formData) {
        // Normalize field order to ensure consistent rendering
        // Standard order: store_id, material_code, quantity
        // VOBB uses different fields: postal_code, state, city, quantity
        // Also convert all values to strings to handle integer values from backend
        const normalizeFields = (sourceData, itemKey) => {
          const fieldOrder = itemKey === 'VOBB'
            ? ['postal_code', 'state', 'city', 'quantity']
            : ['store_id', 'material_code', 'quantity'];
          const normalized = {};
          fieldOrder.forEach(field => {
            const value = sourceData?.[field];
            // Convert to string if value exists, otherwise empty string
            normalized[field] = (value != null && value !== '') ? String(value) : '';
          });
          return normalized;
        };

        if (json_data.formData.formType === "postpaid") {
          // Normalize Postpaid data before setting state
          const normalizedPostpaidData = { ...json_data.formData.postpaidData };
          if (normalizedPostpaidData.Postpaid) {
            // Only normalize items that exist in the payload
            const normalizedPostpaid = {};
            const items = ['MSISDN', 'Blank Sim', 'eSIM', 'VOBB'];
            items.forEach(itemKey => {
              if (normalizedPostpaidData.Postpaid[itemKey]) {
                normalizedPostpaid[itemKey] = normalizeFields(normalizedPostpaidData.Postpaid[itemKey], itemKey);
              }
            });
            normalizedPostpaidData.Postpaid = normalizedPostpaid;
          }
          setPostpaidFormData(normalizedPostpaidData);
          setRequesterInitialData(json_data.formData.RequesterDetails);
          setActiveForm("postpaid");
        } else if (json_data.formData.formType === "prepaid") {
          // Normalize Prepaid data before setting state
          const normalizedPrepaidData = { ...json_data.formData.prepaidData };

          // Normalize Prepaid section (MSISDN, Blank Sim) - only items that exist
          if (normalizedPrepaidData.Prepaid) {
            const normalizedPrepaid = {};
            ['MSISDN', 'Blank Sim'].forEach(itemKey => {
              if (normalizedPrepaidData.Prepaid[itemKey]) {
                normalizedPrepaid[itemKey] = normalizeFields(normalizedPrepaidData.Prepaid[itemKey]);
              }
            });
            normalizedPrepaidData.Prepaid = normalizedPrepaid;
          }

          // Normalize Starter Pack section - only items that exist
          if (normalizedPrepaidData['Starter Pack']) {
            const normalizedStarterPack = {};
            ['RM5', 'RM10', 'RM30', 'Others'].forEach(itemKey => {
              if (normalizedPrepaidData['Starter Pack'][itemKey]) {
                normalizedStarterPack[itemKey] = normalizeFields(normalizedPrepaidData['Starter Pack'][itemKey]);
              }
            });
            normalizedPrepaidData['Starter Pack'] = normalizedStarterPack;
          }

          // Normalize Reload section - only items that exist
          if (normalizedPrepaidData.Reload) {
            const normalizedReload = {};
            ['RM5', 'RM10', 'RM30', 'RM50'].forEach(itemKey => {
              if (normalizedPrepaidData.Reload[itemKey]) {
                normalizedReload[itemKey] = normalizeFields(normalizedPrepaidData.Reload[itemKey]);
              }
            });
            normalizedPrepaidData.Reload = normalizedReload;
          }

          // Normalize Softpin section - only items that exist
          if (normalizedPrepaidData.Softpin) {
            const normalizedSoftpin = {};
            ['RM5', 'RM10', 'RM20', 'RM30', 'RM50', 'RM100'].forEach(itemKey => {
              if (normalizedPrepaidData.Softpin[itemKey]) {
                normalizedSoftpin[itemKey] = normalizeFields(normalizedPrepaidData.Softpin[itemKey]);
              }
            });
            normalizedPrepaidData.Softpin = normalizedSoftpin;
          }

          setPrepaidFormData(normalizedPrepaidData);
          setRequesterInitialData(json_data.formData.RequesterDetails);
          setActiveForm("prepaid");
        }
        return;
      }

      if (Object.keys(json_data).length === 0) {
        console.log("Empty message received, ignoring...");
        return;
      }

      if (json_data.type === 'sr_ticket_response') {
        let ticketMessage = '';
        if (json_data.ticketno) {
          ticketMessage = `Ticket created successfully! Ticket Number: ${json_data.ticketno}`;
        } else if (json_data.error) {
          ticketMessage = `Error creating ticket: ${json_data.error}`;
        } else if (json_data.message) {
          ticketMessage = json_data.message;
        } else {
          ticketMessage = 'Unknown ticket response';
        }

        // Add ticket response message immediately to messages
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: "sr_ticket_response",
            message: ticketMessage,
            status: json_data.ticketno ? 'success' : 'error',
            ticketNo: json_data.ticketno,
            message_id: `ticket_${Date.now()}`
          }
        ]);
        setIsProcessing(false);

        // If ticket was created successfully, close form and enable buttons
        if (json_data.ticketno) {
          // Close the active form
          setActiveForm(null);
          setSplitPosition(40);
          // Enable the form buttons
          if (window.enableFormButtons) {
            window.enableFormButtons();
          }
        }

        // Reset streaming message to ensure clean state
        setStreamingMessage({
          type: "answer",
          message: "",
          message_id: "",
          references: [],
        });
        outputMessage = "";
        outputReferences = [];
        return;
      }

      if (json_data.type === "agent_progress") {
        const progressDetail = json_data.progress;

        setMessages((prevMessages) => {

          if (prevMessages.length === 0) {
            const tempMessage = {
              type: json_data.type,
              progressDetails: [progressDetail],
            };
            return [tempMessage];
          }

          const lastMessage = prevMessages[prevMessages.length - 1];

          if (lastMessage.type !== json_data.type) {
            const tempMessage = {
              type: json_data.type,
              progressDetails: [progressDetail],
            };

            return [
              ...prevMessages,
              tempMessage
            ];
          }

          const updatedLastMessage = {
            ...lastMessage,
            progressDetails: [...lastMessage.progressDetails, progressDetail]
          };

          console.log(JSON.stringify(updatedLastMessage.progressDetails));

          return [
            ...prevMessages.slice(0, -1),
            updatedLastMessage
          ];

        });
      }

      // Handle regular streaming messages
      if (json_data.message === "#END_MESSAGE#") {
        const outputMessage_temp = outputMessage.trim()
        const outputReferencess_temp = outputReferences;

        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: "answer",
            message: outputMessage_temp,
            message_id: json_data.message_id,
            references: outputReferencess_temp || [],
          },
        ]);
        outputMessage = "";
        outputReferences = [];

        setStreamingMessage({
          type: "answer",
          message: "",
          message_id: "",
          references: [],
        });

        setIsProcessing(false);

        if (!document.hasFocus() && Notification.permission === "granted") {
          new Notification("New Message", {
            body: "TDS has sent you a new reply.",
            icon: Logo,
          });
        }
      } else if (json_data.message) {
        // Handle regular streaming message
        // Handle both escaped \n and actual newlines, then convert escaped \n to actual line breaks
        const processedMessage = json_data.message.replace(/\\n/g, '\n')
        outputMessage += processedMessage

        setStreamingMessage((prevMessage) => ({
          ...prevMessage,
          message: outputMessage,
          message_id: json_data.id,
        }));
      }

      if (json_data.references) {
        console.log("References: ", json_data.references);
        outputReferences = json_data.references;
        setStreamingMessage((prevMessage) => ({
          ...prevMessage,
          references: outputReferences,
        }));
      }
    })
  }, [apiKey, email])

  // Handle window resize effect
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth
      setWindowWidth(newWidth)

      // Automatically adjust chat visibility based on screen width
      if (activeForm) {
        // Hide chat on small screens when form is active
        if (newWidth < 1100) {
          setShowChat(false)
        } else {
          // Show both on larger screens
          setShowChat(true)
        }
      }
    }

    window.addEventListener('resize', handleResize)

    // Initial check on mount or when activeForm changes
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [activeForm])

  // Initialize websocket connection
  useEffect(() => {
    requestNotificationPermission()
    connectWebSocket()

    return () => {
      if (ws) {
        ws.close()
      }
    }
  }, [connectWebSocket])

  // activeForm and lastActiveForm have been moved to the top of the component

  const handlePrepaidFormDataChange = (data) => {
    setPrepaidFormData(data);
  };

  const handlePostpaidFormDataChange = (data) => {
    setPostpaidFormData(data);
  };

  // Extending handleFormDisplay to track active form
  const handleFormDisplay = (formType) => {
    setActiveForm(formType);
    if (formType) {
      setLastActiveForm(formType);

      // For window width less than 1100px, hide chat and only show form
      if (windowWidth < 1100) {
        setShowChat(false);
      }
    } else {
      // Always show chat when no form is active
      setShowChat(true);
    }
    handleShowForm(formType);
  };

  return (
    <Stack padding={2} height="90vh" width="100%" alignItems="center">
      <Header websocketStatus={wsStatus} />
      {/* Main Content Area */}
      {/* height: "calc(100vh - 120px)", */}
      <Box
        data-testid="split-container"
        sx={{
          height: "100%",
          width: "100%",
          maxWidth: "2000px",
          display: "flex",
          gap: 2,
          justifyContent: "center",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Chat View - Hide when form is active and window is narrow */}
        {(!activeForm || (activeForm && windowWidth >= 1100) || (activeForm && showChat)) && (
          <SplitView
            messages={messages}
            streamingMessage={streamingMessage}
            messagesEndRef={messagesEndRef}
            handleSendMessage={handleSendMessage}
            isProcessing={isProcessing}
            onShowForm={handleFormDisplay}
            websocket={ws}
            chatWidth={activeForm ? `${splitPosition}%` : windowWidth < 850 ? "95%" : "850px"}
            onSplitChange={setSplitPosition}
            isActiveForm={Boolean(activeForm) && (windowWidth >= 1100 || showChat)}
            style={{
              flex: activeForm ? `0 1 ${splitPosition}%` : windowWidth < 850 ? "0 0 95%" : "0 0 850px",
              maxWidth: activeForm ? `${splitPosition}%` : windowWidth < 850 ? "95%" : "850px",
              margin: activeForm && windowWidth >= 1100 ? "0" : "0 auto"
            }}
          />
        )}

        {/* Re-open button that shows when form is closed */}
        {!activeForm && lastActiveForm && (
          <Tooltip title={`Click to re-open ${lastActiveForm} form`} placement="left" arrow>
            <IconButton
              onClick={() => {
                handleFormDisplay(lastActiveForm);
                setSplitPosition(40);
                // Hide chat on small screens when reopening form
                if (windowWidth < 1100) {
                  setShowChat(false);
                }
              }}
              sx={{
                position: 'absolute',
                right: 0,
                top: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <LuSkipBack size={20} />
            </IconButton>
          </Tooltip>
        )}

        {/* Form View - Only shown when a form is active */}
        {activeForm && (
          <Box
            sx={{
              flex: windowWidth < 1100 && !showChat ? "1 1 100%" : `1 0 ${100 - splitPosition}%`,
              width: windowWidth < 1100 && !showChat ? "100%" : `${100 - splitPosition}%`,
              minWidth: windowWidth < 850 ? "60%" : "450px",
              height: "100%",
              overflowY: "auto",
              bgcolor: "#FFF",
              borderRadius: 1,
              boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
              position: 'relative',
              transition: 'all 0.3s ease-in-out',
            }}
          >

            {/* Close button */}
            <IconButton
              onClick={() => {
                setActiveForm(null);
                setSplitPosition(40);
                setShowChat(true); // Always show chat when closing form
                if (window.enableFormButtons) {
                  window.enableFormButtons();
                }
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                zIndex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <IoClose size={20} />
            </IconButton>

            {activeForm === 'prepaid' && (
              <PrepaidForm
                existingWebSocket={ws}
                wsConnectionId={wsConnectionId}
                initialData={prepaidFormData}
                requesterInitialData={requesterInitialData}
                onFormDataChange={handlePrepaidFormDataChange}
              />
            )}
            {activeForm === 'postpaid' && (
              <PostpaidForm
                existingWebSocket={ws}
                wsConnectionId={wsConnectionId}
                initialData={postpaidFormData}
                requesterInitialData={requesterInitialData}
                onFormDataChange={handlePostpaidFormDataChange}
              />
            )}
          </Box>
        )}
      </Box>
    </Stack>
  )
}

export default GeneralApp

